import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, Info } from "lucide-react";
import { type ReactNode, useState } from "react";

export type InfoBannerVariant = "info" | "success" | "warning" | "danger";

export interface InfoBannerProps {
	variant?: InfoBannerVariant;
	/** The always-visible headline (e.g. the page description). */
	title: ReactNode;
	/** Optional detail, revealed under the title (collapsible when `collapsible`). */
	children?: ReactNode;
	/** Render as an expand/collapse disclosure (chevron). Default false. */
	collapsible?: boolean;
	/** Start expanded when collapsible. Default false. */
	defaultOpen?: boolean;
	/** Override the leading icon. */
	icon?: ReactNode;
	className?: string;
}

const variantStyles: Record<InfoBannerVariant, { box: string; title: string; icon: ReactNode }> = {
	info: {
		box: "border-primary/20 bg-primary-soft",
		title: "text-primary-soft-fg",
		icon: <Info size={16} />,
	},
	success: {
		box: "border-success/20 bg-success-soft",
		title: "text-success",
		icon: <CheckCircle2 size={16} />,
	},
	warning: {
		box: "border-warning/30 bg-warning-soft",
		title: "text-warning",
		icon: <AlertTriangle size={16} />,
	},
	danger: {
		box: "border-danger/20 bg-danger-soft",
		title: "text-danger",
		icon: <AlertCircle size={16} />,
	},
};

/**
 * A tokenized callout/notice box — the standard "what is this page" banner and
 * inline notices. Replaces hand-rolled palette-colored notice cards and the
 * bespoke admin-shell PageHelp markup. All colors resolve to operator-theme tokens.
 */
export function InfoBanner({
	variant = "info",
	title,
	children,
	collapsible = false,
	defaultOpen = false,
	icon,
	className = "",
}: InfoBannerProps) {
	const [open, setOpen] = useState(defaultOpen);
	const s = variantStyles[variant];
	const hasBody = children != null;
	const showBody = hasBody && (!collapsible || open);

	const header = (
		<span className={`flex items-center gap-2 text-sm font-medium ${s.title}`}>
			<span className="shrink-0">{icon ?? s.icon}</span>
			{title}
		</span>
	);

	return (
		<div className={`rounded-lg border ${s.box} ${className}`.trim()}>
			{collapsible && hasBody ? (
				<button
					type="button"
					onClick={() => setOpen((o) => !o)}
					className="flex w-full items-center justify-between px-4 py-2.5 text-left"
				>
					{header}
					<ChevronDown
						size={16}
						className={`shrink-0 ${s.title} transition-transform ${open ? "rotate-180" : ""}`}
					/>
				</button>
			) : (
				<div className="px-4 py-2.5">{header}</div>
			)}
			{showBody && (
				<div
					className={`px-4 py-2.5 text-xs ${s.title} ${collapsible ? "border-t border-current/10" : "pt-0"}`}
				>
					{children}
				</div>
			)}
		</div>
	);
}
